import { QueryClient, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for removing queries from cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type for the queries
 */
export interface RemoveTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, removes all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional query filters to narrow down which queries to remove */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  >;
}

/**
 * Removes queries from the query client cache for matching Tellescope queries.
 * This completely removes the queries from the cache, including their data and state.
 *
 * @param args - The remove queries arguments
 * @returns void
 *
 * @example
 * ```tsx
 * // Remove all queries for a method
 * removeTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Remove specific query
 * removeTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Remove with filters
 * removeTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   filters: {
 *     type: "inactive", // Only remove inactive queries
 *   },
 * });
 *
 * // Remove stale queries
 * removeTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   filters: {
 *     stale: true, // Only remove stale queries
 *   },
 * });
 * ```
 */
export const removeTellescopeQueries = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  filters,
}: RemoveTellescopeQueriesArgs<Model, Method, TError>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  return queryClient.removeQueries({
    queryKey,
    ...filters,
  } as QueryFilters<TellescopeQueryKey<Model, Method>>);
};
